<template lang="pug">
.yz-checkbox(:class="{ 'yz-checkbox--disabled': disabled, 'yz-checkbox--invalid': computedInvalid, 'yz-checkbox--indeterminate': isIndeterminate }")
  label.yz-checkbox__container
    input.yz-checkbox__input(:id="`checkbox-${_uid}`" type="checkbox" :checked="isChecked" :indeterminate="isIndeterminate" :name="computedName" :disabled="disabled" @change="onChange" @focus="onFocus" @blur="onBlur" ref="checkbox")
    .yz-checkbox__box(:style="{ borderRadius: borderRadius }")
      .yz-checkbox__checkmark(v-if="!isIndeterminate")
      .yz-checkbox__indeterminate(v-if="isIndeterminate")
    span.yz-checkbox__label(v-if="label || $slots.label" :style="{ marginLeft: labelGap }" :class="labelClass")
      slot(name="label") {{ label }}
</template>

<script>
export default {
  name: 'YzCheckbox',
  inject: {
    checkboxGroup: {
      from: 'checkboxGroup',
      default: null
    }
  },
  props: {
    value: {
      type: [Boolean, String, Number, Object],
      default: false
    },
    label: {
      type: String,
      default: ''
    },
    name: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    invalid: {
      type: Boolean,
      default: false
    },
    borderRadius: {
      type: String,
      default: '5px'
    },
    labelGap: {
      type: String,
      default: '8px'
    },
    labelClass: {
      type: String,
      default: 'yz-regular-body-sm'
    },
    indeterminate: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    isChecked() {
      if (this.isIndeterminate) {
        return false; // Indeterminate state takes precedence
      }
      if (this.checkboxGroup) {
        return this.checkboxGroup.value.includes(this.value);
      }
      return this.value;
    },
    isIndeterminate() {
      return this.indeterminate;
    },
    computedName() {
      return this.checkboxGroup ? `checkbox-group-${this.checkboxGroup._uid}` : `checkbox-${this._uid}`;
    },
    computedInvalid() {
      return this.invalid || (this.checkboxGroup && this.checkboxGroup.hasError);
    }
  },
  mounted() {
    // Set the native indeterminate property on the DOM element
    this.updateIndeterminateState();
  },
  updated() {
    // Update indeterminate state whenever component updates
    this.updateIndeterminateState();
  },
  methods: {
    onChange() {
      if (!this.disabled) {
        if (this.isIndeterminate) {
          // When indeterminate, clicking should clear the indeterminate state
          this.$emit('update:indeterminate', false);
        }

        if (this.checkboxGroup) {
          this.checkboxGroup.updateValue(this.value, !this.isChecked);
        } else {
          this.$emit('input', !this.value);
          this.$emit('change', !this.value);
        }
      }
    },
    onFocus(event) {
      this.$emit('focus', event);
    },
    onBlur(event) {
      this.$emit('blur', event);
    },
    focus() {
      this.$refs.checkbox.focus();
    },
    blur() {
      this.$refs.checkbox.blur();
    },
    updateIndeterminateState() {
      // Set the native indeterminate property on the input element
      if (this.$refs.checkbox) {
        this.$refs.checkbox.indeterminate = this.isIndeterminate;
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.yz-checkbox {
  position: relative;
  display: inline-flex;
  align-items: center;
  font-family: $body-font-family;

  &__container {
    display: inline-flex;
    align-items: center;
    cursor: pointer;

    &:hover .yz-checkbox__box {
      border-color: var(--yz-neutral-500);
    }
  }

  &__input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;

    &:checked ~ .yz-checkbox__box {
      border-color: var(--yz-neutral-500);
      background-color: var(--yz-neutral-700);

      .yz-checkbox__checkmark {
        opacity: 1;
        transform: rotate(45deg) scale(1);
      }
    }

    &:indeterminate ~ .yz-checkbox__box {
      border-color: var(--yz-neutral-500);
      background-color: var(--yz-neutral-700);

      .yz-checkbox__indeterminate {
        opacity: 1;
        transform: scale(1);
      }
    }

    &:focus ~ .yz-checkbox__box {
      box-shadow: 0 0 0 2px var(--yz-neutral-300);
    }
  }

  &__box {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border: 1px solid var(--yz-neutral-400);
    background-color: var(--yz-neutral-300);
    transition: all 0.2s ease;
    box-sizing: border-box;
  }

  &__checkmark {
    position: relative;
    width: 4px;
    height: 10px;
    border-right: 2px solid var(--yz-neutral-100);
    border-bottom: 2px solid var(--yz-neutral-100);
    opacity: 0;
    transform: rotate(45deg) scale(0);
    transition: all 0.2s ease;
    top: -1px; // Move up by 1px
  }

  &__indeterminate {
    position: relative;
    width: 8px;
    height: 2px;
    background-color: var(--yz-neutral-100);
    opacity: 0;
    transform: scale(0);
    transition: all 0.2s ease;
  }

  &__label {
    color: var(--yz-neutral-700);
  }

  &--disabled {
    .yz-checkbox__container {
      cursor: not-allowed;
    }

    .yz-checkbox__box {
      background-color: var(--yz-neutral-200);
      border-color: var(--yz-neutral-400);
    }

    .yz-checkbox__container:hover .yz-checkbox__box {
      border-color: var(--yz-neutral-400);
    }

    .yz-checkbox__input:checked ~ .yz-checkbox__box {
      background-color: var(--yz-neutral-500);
      border-color: transparent;

      .yz-checkbox__checkmark {
        border-color: var(--yz-neutral-100);
      }
    }

    .yz-checkbox__input:indeterminate ~ .yz-checkbox__box {
      background-color: var(--yz-neutral-500);
      border-color: transparent;

      .yz-checkbox__indeterminate {
        background-color: var(--yz-neutral-100);
      }
    }

    .yz-checkbox__label {
      color: var(--yz-neutral-500);
    }
  }

  &--invalid {
    .yz-checkbox__box {
      background-color: var(--yz-neutral-100);
      border-color: var(--yz-danger);
    }

    .yz-checkbox__container:hover .yz-checkbox__box {
      background-color: var(--yz-neutral-100);
      border-color: var(--yz-danger);
    }

    .yz-checkbox__input:checked ~ .yz-checkbox__box {
      background-color: var(--yz-neutral-100);
      border-color: var(--yz-danger);

      .yz-checkbox__checkmark {
        border-color: var(--yz-danger);
      }
    }

    .yz-checkbox__input:indeterminate ~ .yz-checkbox__box {
      background-color: var(--yz-neutral-100);
      border-color: var(--yz-danger);

      .yz-checkbox__indeterminate {
        background-color: var(--yz-danger);
      }
    }
  }

  &--indeterminate {
    .yz-checkbox__box {
      border-color: var(--yz-neutral-500);
      background-color: var(--yz-neutral-700);
    }

    .yz-checkbox__indeterminate {
      opacity: 1;
      transform: scale(1);
    }
  }
}
</style>
